import React, { useCallback, useEffect, useRef, useState } from "react";
import { fetchLogsForAgent, fetchAllLogs, fetchLogFilterOptions } from "../../services/activityLogService";
import { getCurrentUser } from "../../services/authService";

const TYPE_LABELS = {
  Login:           "Prijava u sustav",
  PrintPolicies:   "Isprintane police osiguranja",
  AddPolicy:       "Dodana nova polica osiguranja",
  AddClient:       "Dodan novi ugovaratelj",
  UpdatePolicy:    "Izmjenjeni detalji police osiguranja",
  UpdateClient:    "Izmjenjeni detalji ugovaratelja",
  DeletePolicy:    "Obrisana polica osiguranja",
  RestorePolicy:   "Polica vraćena iz smeća",
  ExtendPolicy:    "Produžena polica osiguranja",
  ChangeClient:    "Izmjenjen ugovaratelj na polici osiguranja",
  AddAgent:        "Dodan novi djelatnik",
  UpdateAgent:     "Izmjenjene informacije o djelatniku",
  DeactivateAgent: "Deaktiviran djelatnik",
  ActivateAgent:   "Aktiviran djelatnik",
  ChangePassword:  "Promjenjena lozinka",
};

const TZ = { timeZone: "Europe/Zagreb" };

function formatDate(dateStr) {
  if (!dateStr) return { date: "", time: "" };
  const d = new Date(dateStr);
  const date = d.toLocaleDateString("hr-HR", { day: "2-digit", month: "2-digit", year: "numeric", ...TZ });
  const time = d.toLocaleTimeString("hr-HR", { hour: "2-digit", minute: "2-digit", second: "2-digit", ...TZ }) + "h";
  return { date, time };
}

function parseDetails(details) {
  if (!details) return null;
  try { return JSON.parse(details); } catch { return null; }
}

function get(obj, ...keys) {
  for (const k of keys) if (obj[k] !== undefined && obj[k] !== null) return obj[k];
  return undefined;
}

function hasExpandableContent(entry, parsed) {
  if (!parsed) return false;
  if (entry.activityType === "PrintPolicies") return get(parsed, "count", "Count") !== undefined;
  const changes = get(parsed, "changes", "Changes");
  return Array.isArray(changes) && changes.length > 0;
}

function ExpandedDetails({ entry, parsed }) {
  if (!parsed) return null;

  if (entry.activityType === "PrintPolicies") {
    const v   = (a, b) => get(parsed, a, b);
    const arr = (a, b) => { const x = v(a, b); return Array.isArray(x) ? x : null; };
    const rows = [
      ["Isprintano polica", v("count", "Count")],
      ["Godina",            v("year", "Year")],
      ["Status",            v("status", "Status") || null],
      ["Pretraga",          v("search", "Search") || null],
      ["Datum od",          v("dateFrom", "DateFrom") || null],
      ["Datum do",          v("dateTo", "DateTo") || null],
      ["Premija od",        v("priceFrom", "PriceFrom") ? `${v("priceFrom", "PriceFrom")} €` : null],
      ["Premija do",        v("priceTo", "PriceTo") ? `${v("priceTo", "PriceTo")} €` : null],
      ["Osiguravajuća kuća", arr("insuranceCompany", "InsuranceCompany")?.join(", ")],
      ["Vrsta osiguranja",   arr("insuranceType", "InsuranceType")?.join(", ")],
      ["Prodajno mjesto",    arr("location", "Location")?.join(", ")],
      ["Partner",            arr("partner", "Partner")?.join(", ")],
    ].filter(([, val]) => val !== null && val !== undefined && val !== "");

    return (
      <ul className="al-details-list">
        {rows.map(([k, val]) => (
          <li key={k}><span className="al-detail-key">{k}:</span> <strong>{val}</strong></li>
        ))}
      </ul>
    );
  }

  const changes = get(parsed, "changes", "Changes");
  if (Array.isArray(changes) && changes.length > 0) {
    return (
      <ul className="al-details-list">
        {changes.map((c, i) => (
          <li key={i} className="al-change-row">
            <span className="al-detail-key">{c.field ?? c.Field}:</span>
            <span className="al-val-old">{c.from ?? c.From ?? "—"}</span>
            <span className="al-arrow">→</span>
            <span className="al-val-new">{c.to ?? c.To ?? "—"}</span>
          </li>
        ))}
      </ul>
    );
  }
  return null;
}

function LogRow({ entry, showAgent }) {
  const [open, setOpen] = useState(false);
  const label  = TYPE_LABELS[entry.activityType] ?? entry.activityType;
  const parsed = parseDetails(entry.details);
  const canClick = hasExpandableContent(entry, parsed);
  const { date, time } = formatDate(entry.createdAt);

  return (
    <tr
      className={`al-tr${canClick ? " al-tr--clickable" : ""}${open ? " al-tr--open" : ""}`}
      onClick={canClick ? () => setOpen(o => !o) : undefined}
    >
      <td className="al-td al-td-date">
        <span className="al-date-main">{date}</span>
        <span className="al-date-time">{time}</span>
      </td>
      {showAgent && (
        <td className="al-td al-td-agent">
          <span className="al-agent-name">{entry.agentName || "—"}</span>
        </td>
      )}
      <td className="al-td al-td-action">
        <span className="al-action-title">{label}</span>
        <span className="al-action-desc">{entry.description}</span>
        {open && canClick && <ExpandedDetails entry={entry} parsed={parsed} />}
      </td>
    </tr>
  );
}

const TYPE_LABEL_HR = {
  Login:           "Prijava u sustav",
  PrintPolicies:   "Isprintane police",
  AddPolicy:       "Dodana polica",
  AddClient:       "Dodan ugovaratelj",
  UpdatePolicy:    "Izmjena police",
  UpdateClient:    "Izmjena ugovaratelja",
  DeletePolicy:    "Obrisana polica",
  RestorePolicy:   "Vraćena polica",
  ExtendPolicy:    "Produžena polica",
  ChangeClient:    "Izmjena ugovaratelja na polici",
  AddAgent:        "Dodan djelatnik",
  UpdateAgent:     "Izmjena djelatnika",
  DeactivateAgent: "Deaktiviran djelatnik",
  ActivateAgent:   "Aktiviran djelatnik",
  ChangePassword:  "Promjena lozinke",
};

export default function ActivityLogSection({ agentId = null, showAll = false }) {
  const [items, setItems]           = useState([]);
  const [pageNumber, setPageNumber] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState("");
  const [printing, setPrinting]     = useState(false);

  const [agentNames,    setAgentNames]    = useState([]);
  const [activityTypes, setActivityTypes] = useState([]);

  const [filterAgent,    setFilterAgent]    = useState("");
  const [filterType,     setFilterType]     = useState("");
  const [filterSearch,   setFilterSearch]   = useState("");
  const [filterDateFrom, setFilterDateFrom] = useState("");
  const [filterDateTo,   setFilterDateTo]   = useState("");

  const searchTimeout = useRef(null);
  const PAGE_SIZE = 20;

  useEffect(() => {
    if (!showAll && !agentId) return;
    fetchLogFilterOptions()
      .then(data => {
        setAgentNames(data.agentNames ?? []);
        setActivityTypes(data.activityTypes ?? []);
      })
      .catch(() => {});
  }, [showAll, agentId]);

  const filter = {
    agentName:    filterAgent,
    activityType: filterType,
    search:       filterSearch,
    dateFrom:     filterDateFrom,
    dateTo:       filterDateTo,
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = showAll
        ? await fetchAllLogs(pageNumber, PAGE_SIZE, filter)
        : await fetchLogsForAgent(agentId, pageNumber, PAGE_SIZE, filter);
      setItems((data.items ?? data.Items ?? []).map(i => ({
        id:           i.id           ?? i.Id,
        activityType: i.activityType ?? i.ActivityType,
        description:  i.description  ?? i.Description,
        details:      i.details      ?? i.Details,
        createdAt:    i.createdAt    ?? i.CreatedAt,
        agentName:    i.agentName    ?? i.AgentName ?? "",
      })));
      setTotalPages(data.totalPages ?? data.TotalPages ?? 1);
    } catch (err) {
      setError(err.message || "Greška pri dohvaćanju logova.");
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [agentId, showAll, pageNumber, filterAgent, filterType, filterSearch, filterDateFrom, filterDateTo]);

  useEffect(() => {
    if (showAll || agentId) load();
  }, [load, showAll, agentId]);

  const resetPage = () => setPageNumber(1);

  const handleSearch = (val) => {
    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => {
      setFilterSearch(val);
      resetPage();
    }, 350);
  };

  const handleFilterAgent = (v) => { setFilterAgent(v); resetPage(); };
  const handleFilterType  = (v) => { setFilterType(v);  resetPage(); };
  const handleDateFrom    = (v) => { setFilterDateFrom(v); resetPage(); };
  const handleDateTo      = (v) => { setFilterDateTo(v);   resetPage(); };

  const handlePrint = async () => {
    const win = window.open("", "_blank");
    if (!win) {
      alert("Pop-up prozor je blokiran. Dozvoli pop-up prozore za ovu stranicu i pokušaj ponovo.");
      return;
    }
    setPrinting(true);
    try {
      const data = showAll
        ? await fetchAllLogs(1, 5000, filter)
        : await fetchLogsForAgent(agentId, 1, 5000, filter);
      const allItems = (data.items ?? data.Items ?? []).map(i => ({
        activityType: i.activityType ?? i.ActivityType,
        description:  i.description  ?? i.Description,
        details:      i.details      ?? i.Details ?? null,
        createdAt:    i.createdAt    ?? i.CreatedAt,
        agentName:    i.agentName    ?? i.AgentName ?? "",
      }));

      const user = getCurrentUser();
      const printedAt = new Date().toLocaleString("hr-HR", TZ);

      const criteriaRows = [
        showAll && filterAgent   ? ["Korisnik", filterAgent]       : null,
        filterType               ? ["Radnja", TYPE_LABEL_HR[filterType] ?? filterType] : null,
        filterSearch             ? ["Pretraga", filterSearch]       : null,
        filterDateFrom           ? ["Datum od", new Date(filterDateFrom).toLocaleDateString("hr-HR")] : null,
        filterDateTo             ? ["Datum do", new Date(filterDateTo).toLocaleDateString("hr-HR")]   : null,
      ].filter(Boolean);

      const criteriaHtml = criteriaRows.length
        ? `<div class="criteria">${criteriaRows.map(([k, v]) =>
            `<span class="crit"><strong>${k}:</strong> ${v}</span>`).join("")}</div>`
        : "";

      const formatDt = (dateStr) => {
        if (!dateStr) return "-";
        const d = new Date(dateStr);
        return d.toLocaleString("hr-HR", { day: "2-digit", month: "2-digit", year: "numeric",
          hour: "2-digit", minute: "2-digit", ...TZ });
      };

      const renderDetailsHtml = (e) => {
        if (!e.details) return "";
        let parsed;
        try { parsed = JSON.parse(e.details); } catch { return ""; }

        if (e.activityType === "PrintPolicies") {
          const v = (...keys) => { for (const k of keys) if (parsed[k] !== undefined && parsed[k] !== null) return parsed[k]; return undefined; };
          const arr = (...keys) => { const x = v(...keys); return Array.isArray(x) ? x : null; };
          const detRows = [
            ["Isprintano polica", v("count", "Count")],
            ["Godina",            v("year", "Year")],
            ["Status",            v("status", "Status") || null],
            ["Pretraga",          v("search", "Search") || null],
            ["Datum od",          v("dateFrom", "DateFrom") || null],
            ["Datum do",          v("dateTo", "DateTo") || null],
            ["Premija od",        v("priceFrom", "PriceFrom") ? `${v("priceFrom", "PriceFrom")} €` : null],
            ["Premija do",        v("priceTo", "PriceTo") ? `${v("priceTo", "PriceTo")} €` : null],
            ["Osiguravajuća kuća", arr("insuranceCompany", "InsuranceCompany")?.join(", ")],
            ["Vrsta osiguranja",   arr("insuranceType", "InsuranceType")?.join(", ")],
            ["Prodajno mjesto",    arr("location", "Location")?.join(", ")],
            ["Partner",            arr("partner", "Partner")?.join(", ")],
          ].filter(([, val]) => val !== null && val !== undefined && val !== "");
          if (!detRows.length) return "";
          return `<ul class="det-list">${detRows.map(([k, val]) =>
            `<li><span class="det-key">${k}:</span> ${val}</li>`).join("")}</ul>`;
        }

        const changes = parsed.changes ?? parsed.Changes;
        if (!Array.isArray(changes) || !changes.length) return "";
        return `<ul class="det-list">${changes.map(c =>
          `<li><span class="det-key">${c.field ?? c.Field}:</span> <span class="det-old">${c.from ?? c.From ?? "—"}</span> <span class="det-arrow">→</span> <span class="det-new">${c.to ?? c.To ?? "—"}</span></li>`
        ).join("")}</ul>`;
      };

      const agentCol = showAll ? "<th>Djelatnik</th>" : "";
      const rows = allItems.map(e => {
        const detHtml = renderDetailsHtml(e);
        return `
        <tr>
          <td>${formatDt(e.createdAt)}</td>
          ${showAll ? `<td>${e.agentName || "—"}</td>` : ""}
          <td>${TYPE_LABEL_HR[e.activityType] ?? e.activityType}</td>
          <td>${e.description}${detHtml}</td>
        </tr>`;
      }).join("");

      const html = `<!DOCTYPE html>
<html lang="hr">
<head>
  <meta charset="utf-8" />
  <title>Activity logovi</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: Arial, sans-serif; font-size: 11px; padding: 20px; color: #111; position: relative; }
    h1 { font-size: 15px; margin-bottom: 6px; }
    .meta { font-size: 10px; color: #555; margin-bottom: 8px; }
    .criteria { display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 14px; }
    .crit { background: #f1f3f9; border: 1px solid #d8dce8; border-radius: 4px; padding: 2px 8px; font-size: 10px; color: #333; }
    table { width: 100%; border-collapse: collapse; }
    th { background: #1a1a2e; color: #fff; padding: 6px 8px; text-align: left; font-size: 10px; }
    td { padding: 5px 8px; border-bottom: 1px solid #e0e0e0; vertical-align: top; }
    tr:nth-child(even) td { background: #f8f8f8; }
    td:last-child { max-width: 380px; word-break: break-word; }
    .det-list { margin-top: 5px; padding-left: 0; list-style: none; display: flex; flex-direction: column; gap: 2px; }
    .det-list li { font-size: 10px; color: #444; }
    .det-key { font-weight: 600; color: #222; }
    .det-old { color: #b91c1c; }
    .det-new { color: #15803d; }
    .det-arrow { color: #888; margin: 0 3px; }
    .watermark { position: fixed; top: 50%; left: 50%; transform: translate(-50%, -50%) rotate(-35deg);
      font-size: 22px; font-weight: 700; color: #000; opacity: 0.07; white-space: nowrap;
      pointer-events: none; letter-spacing: 2px; z-index: 0; }
    @page { size: A4 landscape; margin: 15mm; }
    @media print { body { padding: 0; } }
  </style>
</head>
<body>
  <div class="watermark">${user?.id || user?.username || ""}</div>
  <h1>Activity logovi</h1>
  <div class="meta">
    Ispisao/la: <strong>${user?.username || "—"}</strong>
    &nbsp;|&nbsp; Datum ispisa: <strong>${printedAt}</strong>
    &nbsp;|&nbsp; Ukupno: <strong>${allItems.length} zapisa</strong>
  </div>
  ${criteriaHtml}
  <table>
    <thead>
      <tr>
        <th>Datum i vrijeme</th>
        ${agentCol}
        <th>Radnja</th>
        <th>Opis</th>
      </tr>
    </thead>
    <tbody>${rows}</tbody>
  </table>
  <script>window.onload = () => window.print();<\/script>
</body>
</html>`;

      const blob = new Blob([html], { type: "text/html" });
      win.location.href = URL.createObjectURL(blob);
    } catch {
      win.close();
      alert("Greška pri dohvaćanju activity logova za ispis.");
    } finally {
      setPrinting(false);
    }
  };

  return (
    <div className="al-wrap">
      <div className="al-toolbar">
        <div className="al-toolbar-row">
        <div className="al-filters">
          {showAll && agentNames.length > 0 && (
            <div className="al-select-wrap">
              <select className="al-select" value={filterAgent} onChange={e => handleFilterAgent(e.target.value)}>
                <option value="">Korisnik</option>
                {agentNames.map(n => <option key={n} value={n}>{n}</option>)}
              </select>
              <svg className="al-chevron" viewBox="0 0 16 16" fill="none"><path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
            </div>
          )}
          {activityTypes.length > 0 && (
            <div className="al-select-wrap">
              <select className="al-select" value={filterType} onChange={e => handleFilterType(e.target.value)}>
                <option value="">Radnja</option>
                {activityTypes.map(t => <option key={t} value={t}>{TYPE_LABELS[t] ?? t}</option>)}
              </select>
              <svg className="al-chevron" viewBox="0 0 16 16" fill="none"><path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
            </div>
          )}
          <input
            className="al-search"
            type="text"
            placeholder="Pretraga po broju police..."
            onChange={e => handleSearch(e.target.value)}
          />
          <input
            className="al-date-input"
            type="date"
            title="Datum od"
            value={filterDateFrom}
            onChange={e => handleDateFrom(e.target.value)}
          />
          <input
            className="al-date-input"
            type="date"
            title="Datum do"
            value={filterDateTo}
            onChange={e => handleDateTo(e.target.value)}
          />
        </div>
        <button
          type="button"
          className="al-print-btn"
          onClick={handlePrint}
          disabled={printing}
          title="Ispiši activity logove"
        >
          <svg viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M5 7V3h10v4M5 15H3a1 1 0 0 1-1-1V9a1 1 0 0 1 1-1h14a1 1 0 0 1 1 1v5a1 1 0 0 1-1 1h-2m-9 0v3h8v-3H5Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          {printing ? "Priprema..." : "Ispiši"}
        </button>
        </div>
      </div>

      {loading && <p className="al-msg">Učitavanje aktivnosti...</p>}
      {!loading && error && <p className="al-msg al-msg--err">{error}</p>}

      {!loading && !error && items.length === 0 && (
        <div className="employee-empty-state">
          <img src="/images/chart-dynamic-color.png" alt="" />
          <h3>Nema aktivnosti</h3>
          <p>Nema aktivnosti koje odgovaraju odabranim filterima.</p>
        </div>
      )}

      {!loading && !error && items.length > 0 && (
        <div className="al-table-outer">
          <div className="al-table-scroll">
            <table className="al-table">
              <thead>
                <tr>
                  <th className="al-th al-th--date">Datum</th>
                  {showAll && <th className="al-th al-th--agent">Djelatnik</th>}
                  <th className="al-th al-th--action">Radnja</th>
                </tr>
              </thead>
              <tbody>
                {items.map(entry => (
                  <LogRow key={entry.id} entry={entry} showAgent={showAll} />
                ))}
              </tbody>
            </table>
          </div>

          <div className="al-info-panel">
            <p className="al-info-title">Activity logovi za djelatnike</p>
            <p className="al-info-body">
              Tablica za praćenje aktivnosti svih djelatnika u aplikaciji kako bi se
              identificirale greške, riješili problemi, te vidjela produktivnost djelatnika.
            </p>
            <p className="al-info-note">Activity logovi su u demo fazi razvoja.</p>
          </div>
        </div>
      )}

      {!loading && totalPages > 1 && (
        <div className="al-pagination">
          <button disabled={pageNumber <= 1} onClick={() => setPageNumber(p => p - 1)}>← Prethodna</button>
          <span>{pageNumber} / {totalPages}</span>
          <button disabled={pageNumber >= totalPages} onClick={() => setPageNumber(p => p + 1)}>Sljedeća →</button>
        </div>
      )}
    </div>
  );
}
